
import React, { useEffect, useState } from 'react';
import { Box, TableBody, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import {
  MainContainer,
  HeaderContainer,
  PageTitle,
  CreateButton,
  StyledTableContainer,
  StyledTable,
  StyledTableHead,
  StyledTableRow,
  StyledTableCell,
  ActionButton,
} from './styles';
import CustomPagination from '../../../components/CustomPagination';
import { useBreadcrumb } from '../../../context/BreadcrumbContext';
import { useSidebar } from '../../../context/SidebarContext';
import ConfigModal from './components/ConfigModal';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../../../store';
import { listConfigs, deleteConfig } from '../../../modules/masterdata/actions';
import {
  selectListConfigsData,
  selectCreateConfigSuccess,
  selectCreateConfigError,
  selectUpdateConfigSuccess,
  selectUpdateConfigError,
  selectDeleteConfigSuccess,
  selectDeleteConfigError
} from '../../../modules/masterdata/selectors';
import {
  resetCreateConfigState,
  resetUpdateConfigState,
  resetDeleteConfigState,
} from '../../../modules/masterdata/reducers';
import { IConfig } from '../../../modules/masterdata/types';
import { selectOrgProfile } from '../../../modules/orgProfile/selectors';
import ConfirmationDialog from '../../../components/common/ConfirmationDialog';
import AlertPopup from '../../../components/common/AlertPopup';

const Config: React.FC = () => {
  const { setActivePage } = useSidebar();
  const { setPath, setIsShow } = useBreadcrumb();
  const dispatch = useDispatch<AppDispatch>();

  const configs = useSelector(selectListConfigsData);
  const orgProfile = useSelector(selectOrgProfile);

  // Success/Error Selectors
  const createSuccess = useSelector(selectCreateConfigSuccess);
  const createError = useSelector(selectCreateConfigError);
  const updateSuccess = useSelector(selectUpdateConfigSuccess);
  const updateError = useSelector(selectUpdateConfigError);
  const deleteSuccess = useSelector(selectDeleteConfigSuccess);
  const deleteError = useSelector(selectDeleteConfigError);

  const PAGE_SIZE = 10;

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedConfig, setSelectedConfig] = useState<IConfig | null>(null);

  // Confirmation Dialog state
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [deleteKey, setDeleteKey] = useState<string | null>(null);

  // Alert state
  const [alert, setAlert] = useState<{ message: string, severity: 'success' | 'error' } | null>(null);

  const [page, setPage] = useState(1);

  // Client-side pagination
  const [paginatedConfigs, setPaginatedConfigs] = useState<IConfig[]>([]);

  useEffect(() => {
    setActivePage('config');
    setPath([
      { title: 'Settings', path: '/master-data' },
      { title: 'Master Data', path: '/master-data' },
      { title: 'Config', path: '/master-data/config' },
    ]);
    setIsShow(true);
  }, [setActivePage, setPath, setIsShow]);

  useEffect(() => {
    if (orgProfile?.org_id) {
      dispatch(listConfigs(orgProfile.org_id));
    }
  }, [dispatch, orgProfile]);


  useEffect(() => {
    if (configs) {
      const startIndex = (page - 1) * PAGE_SIZE;
      const endIndex = startIndex + PAGE_SIZE;
      setPaginatedConfigs(configs.slice(startIndex, endIndex));
    }
  }, [configs, page]);


  // Handle Notifications and Resets
  useEffect(() => {
    if (createSuccess) {
      setAlert({ message: 'Config created successfully', severity: 'success' });
      if (orgProfile?.org_id) dispatch(listConfigs(orgProfile.org_id));
      dispatch(resetCreateConfigState());
    }
    if (createError) {
      setAlert({ message: createError, severity: 'error' });
      dispatch(resetCreateConfigState());
    }

    if (updateSuccess) {
      setAlert({ message: 'Config updated successfully', severity: 'success' });
      if (orgProfile?.org_id) dispatch(listConfigs(orgProfile.org_id));
      dispatch(resetUpdateConfigState());
    }
    if (updateError) {
      setAlert({ message: updateError, severity: 'error' });
      dispatch(resetUpdateConfigState());
    }

    if (deleteSuccess) {
      setAlert({ message: 'Config deleted successfully', severity: 'success' });
      if (orgProfile?.org_id) dispatch(listConfigs(orgProfile.org_id));
      dispatch(resetDeleteConfigState());
    }
    if (deleteError) {
      setAlert({ message: deleteError, severity: 'error' });
      dispatch(resetDeleteConfigState());
    }
  }, [createSuccess, createError, updateSuccess, updateError, deleteSuccess, deleteError, dispatch, orgProfile]);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleCreateClick = () => {
    setSelectedConfig(null);
    setIsModalOpen(true);
  };

  const handleEditClick = (config: IConfig) => {
    setSelectedConfig(config);
    setIsModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (deleteKey) {
      await dispatch(deleteConfig(deleteKey));
      setIsConfirmOpen(false);
      setDeleteKey(null);
    }
  };

  const totalPages = Math.ceil((configs?.length || 0) / PAGE_SIZE);

  return (
    <Box>
      {alert && <AlertPopup message={alert.message} severity={alert.severity} open={!!alert} onClose={() => setAlert(null)} />}
      <MainContainer>
        <HeaderContainer>
          <PageTitle variant="h6">
            Manage Configs
            <Typography variant="body2" color="error">(If you are not aware of this, please don't update)</Typography>
          </PageTitle>

          <CreateButton variant="contained" disableElevation onClick={handleCreateClick}>
            Create Config
          </CreateButton>
        </HeaderContainer>

        <StyledTableContainer>
          <StyledTable>
            <StyledTableHead>
              <StyledTableRow>
                <StyledTableCell>Id</StyledTableCell>
                <StyledTableCell>Key</StyledTableCell>
                <StyledTableCell>Value</StyledTableCell>
                <StyledTableCell>Action</StyledTableCell>
              </StyledTableRow>
            </StyledTableHead>
            <TableBody>
              {paginatedConfigs && paginatedConfigs.length > 0 ? (
                paginatedConfigs.map((config: IConfig, index: number) => (
                  <StyledTableRow key={config.key}>
                    <StyledTableCell>{config.id}</StyledTableCell>
                    <StyledTableCell>{config.key}</StyledTableCell>
                    <StyledTableCell>
                      {typeof config.value === 'object'
                        ? JSON.stringify(config.value).substring(0, 50) + (JSON.stringify(config.value).length > 50 ? '...' : '')
                        : String(config.value)}
                    </StyledTableCell>
                    <StyledTableCell>
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <ActionButton onClick={() => handleEditClick(config)}>
                          <EditIcon fontSize="small" />
                        </ActionButton>
                      </Box>

                    </StyledTableCell>
                  </StyledTableRow>
                ))
              ) : (
                <StyledTableRow>
                  <StyledTableCell colSpan={5} align="center">No configs found.</StyledTableCell>
                </StyledTableRow>
              )}
            </TableBody>
          </StyledTable>
        </StyledTableContainer>

        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <CustomPagination
            totalPages={totalPages > 0 ? totalPages : 1}
            currentPage={page}
            onPageChange={handlePageChange}
          />
        </Box>
      </MainContainer>

      <ConfigModal
        open={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
        }}
        config={selectedConfig}
      />

      <ConfirmationDialog
        open={isConfirmOpen}
        onClose={() => setIsConfirmOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Config"
        message="Are you sure you want to delete this config?"
      />
    </Box>
  );
};

export default Config;
